import {
  catchError,
  filter,
  map,
  Observable,
  of,
  pipe,
  switchMap,
  tap,
  withLatestFrom,
} from 'rxjs';
import { StringLiteralCheck } from '../../util/string-literal-check';
import {
  createActionGroup,
  createFeature,
  createFeatureSelector,
  createReducer,
  createSelector,
  emptyProps,
  on,
  props,
  select,
  Store,
} from '@ngrx/store';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { PageDataMode, PageDataState } from './page-data.types';

// Note: if needed, cache expiration can easily be implemented by storing the
// last timestamp in the state

export interface PageDataEffectConfig<TParams, TData> {
  load: (params: TParams) => Observable<TData>;
  notifyError?: (params: TParams, error: unknown) => void;
}

/**
 * @param featureName Literal string to be used as the feature name and actions source
 * @returns NGRX feature, actions, pipes, and effect builder.
 */
export function createPageDataFeature<
  TActionSource extends string & StringLiteralCheck<TActionSource, 'source'>,
  TParams,
  TData,
>(featureName: TActionSource) {
  const actions = createActionGroup({
    source: featureName,
    events: {
      clear: emptyProps(),
      load: props<{
        dataParams: TParams;
        suppressErrorNotification?: boolean;
        useLastDataParams?: boolean;
      }>(),
      ensure: props<{
        dataParams: TParams;
        suppressErrorNotification?: boolean;
      }>(),
      transform: props<{ transformation: (data: TData) => TData }>(),
      success: props<{ dataParams: TParams; data: TData }>(),
      fail: props<{
        error: unknown;
        dataParams: TParams;
        suppressErrorNotification?: boolean;
      }>(),
    },
  });
  const feature = createFeature({
    name: featureName,
    reducer: createReducer<PageDataState<TParams, TData>>(
      { mode: PageDataMode.standBy },
      on(actions.load, (state, action) => ({
        mode: PageDataMode.loading,
        dataParams: action.dataParams,
      })),
      on(actions.success, (state, action) =>
        state.mode === PageDataMode.loading &&
        action.dataParams === state.dataParams
          ? {
              mode: PageDataMode.loaded,
              dataParams: action.dataParams,
              data: action.data,
            }
          : state,
      ),
      on(actions.fail, (state, action) =>
        state.mode === PageDataMode.loading &&
        action.dataParams === state.dataParams
          ? {
              mode: PageDataMode.error,
              dataParams: action.dataParams,
            }
          : state,
      ),
      on(actions.clear, () => ({
        mode: PageDataMode.standBy,
      })),
      on(actions.transform, (state, action) =>
        state.mode === PageDataMode.loaded
          ? { ...state, data: action.transformation(state.data) }
          : state,
      ),
    ),
    extraSelectors: baseSelectors => ({
      selectIsLoading: createSelector(
        baseSelectors.selectMode,
        mode => mode === PageDataMode.loading,
      ),
      selectIsLoaded: createSelector(
        baseSelectors.selectMode,
        mode => mode === PageDataMode.loaded,
      ),
      selectIsError: createSelector(
        baseSelectors.selectMode,
        mode => mode === PageDataMode.error,
      ),
    }),
  });
  const selectFeature =
    createFeatureSelector<PageDataState<TParams, TData>>(featureName);
  const selectors = {
    selectIsLoading: feature.selectIsLoading,
    selectIsLoaded: feature.selectIsLoaded,
    selectIsError: feature.selectIsError,
    selectPageDataState: selectFeature,
  };
  const pipes = {
    selectData: pipe(
      select(selectFeature),
      switchMap(state => {
        if (state.mode === PageDataMode.loaded) {
          return of(state.data);
        }
        if (
          state.mode === PageDataMode.standBy ||
          state.mode === PageDataMode.error
        ) {
          return of(null);
        }
        if (state.mode === PageDataMode.loading) {
          return of(); // skip "loading", because it's OK to show previous data
        }
        const exhaustivenessCheck: never = state;
        return exhaustivenessCheck;
      }),
    ),
  };
  const createEffects = (
    action$: Actions,
    store: Store,
    config: PageDataEffectConfig<TParams, TData>,
  ) => {
    const { load, notifyError } = config;
    return {
      load$: createEffect(() =>
        action$.pipe(
          ofType(actions.load),
          withLatestFrom(store.select(selectFeature)),
          switchMap(([action, state]) => {
            // If action specifies useLastDataParams: true and if data params
            // are available in current state, use the data params of the
            // state.  Otherwise, use the data params of the action.
            let dataParams = action.dataParams;
            if (
              action.useLastDataParams &&
              state.mode !== PageDataMode.standBy
            ) {
              dataParams = state.dataParams;
            }
            return load(dataParams).pipe(
              map(data =>
                actions.success({ dataParams: action.dataParams, data }),
              ),
              catchError(error => {
                console.debug('Page data loading error', error);
                return of(
                  actions.fail({
                    error,
                    dataParams: action.dataParams,
                    suppressErrorNotification: action.suppressErrorNotification,
                  }),
                );
              }),
            );
          }),
        ),
      ),
      ensure$: createEffect(() =>
        action$.pipe(
          ofType(actions.ensure),
          withLatestFrom(store.select(selectFeature)),
          // Dispatch `load` if in the standBy or error mode, i. e. if data is not available and no fetching operation is in progress.
          // of if dataParams has changed
          filter(
            ([action, state]) =>
              state.mode === PageDataMode.standBy ||
              state.mode === PageDataMode.error ||
              state.dataParams !== action.dataParams,
          ),
          map(([action]) =>
            actions.load({
              dataParams: action.dataParams,
              suppressErrorNotification: action.suppressErrorNotification,
            }),
          ),
        ),
      ),
      fail$: createEffect(
        () =>
          action$.pipe(
            ofType(actions.fail),
            tap(action => {
              if (!action.suppressErrorNotification) {
                notifyError?.(action.dataParams, action.error);
              }
            }),
          ),
        { dispatch: false },
      ),
    };
  };
  return { feature, selectors, actions, createEffects, pipes };
}
